import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { EmployeeInfoBanner } from '../components/EmployeeInfoBanner';
import { DashboardTabs, DashboardTab } from '../components/DashboardTabs';
import { MyFeedbackView } from '../components/MyFeedbackView';
import { TeamFeedbackView } from '../components/TeamFeedbackView';
import { useUserProfileContext } from '../context/UserProfileContext';
import { useApiClient } from '../hooks/useApiClient';
import { FeedbackRequest } from '../types/feedback';
import { mapApiFeedbackRequestsToList } from '../utils/mappers';
import '../styles/feedback.css';

export function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [myFeedbackCount, setMyFeedbackCount] = useState<number | null>(null);
  
  // Team feedback data (lifted up to avoid duplicate API calls)
  const [teamRequests, setTeamRequests] = useState<FeedbackRequest[]>([]);
  const [teamRequestsLoading, setTeamRequestsLoading] = useState(true);
  const [teamRefreshTrigger, setTeamRefreshTrigger] = useState(0);

  // User profile from centralized context (no more duplicate API calls)
  const { userProfile, loading: profileLoading } = useUserProfileContext();
  const { apiCall } = useApiClient();

  const hasDirectReports = useMemo(() => 
    (userProfile?.directReports?.length || 0) > 0,
    [userProfile?.directReports?.length]
  );

  // Derive activeTab from URL query param (with manager guard)
  const activeTab: DashboardTab = useMemo(() => {
    const tabParam = searchParams.get('tab');
    return (tabParam === 'team-feedback' && hasDirectReports) ? 'team-feedback' : 'my-feedback';
  }, [searchParams, hasDirectReports]);

  const setActiveTab = (tab: DashboardTab) => {
    if (tab === 'my-feedback') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Fetch team feedback requests when user profile is loaded and has direct reports
  useEffect(() => {
    const fetchTeamRequests = async () => {
      if (userProfile?.employeeId && hasDirectReports) {
        setTeamRequestsLoading(true);
        try {
          const response = await apiCall(
            `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/by-employees?ids=${encodeURIComponent(userProfile.directReports.map((r: any) => r.employeeId).join(','))}`
          );
          if (response.ok) {
            const result = await response.json();
            if (result.data) {
              setTeamRequests(mapApiFeedbackRequestsToList(result.data));
            }
          }
        } catch (error) {
          console.error('Error fetching team feedback:', error);
        } finally {
          setTeamRequestsLoading(false);
        }
      }
    };
    
    fetchTeamRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.employeeId, hasDirectReports, teamRefreshTrigger]);

  // Helper to trigger a refresh of team requests
  const refreshTeamRequests = () => setTeamRefreshTrigger(prev => prev + 1);

  // Show loading spinner while fetching user profile
  if (profileLoading && !userProfile) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-slate-600 animate-spin" />
          <p className="text-slate-600 font-medium">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      <main className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        {/* Profile Header */}
        <EmployeeInfoBanner userProfile={userProfile} loading={profileLoading} />

        {/* Dashboard Tabs */}
        <DashboardTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          myFeedbackCount={myFeedbackCount}
          myFeedbackLoading={myFeedbackCount === null}
          teamFeedbackCount={teamRequests.filter(r => r.status !== 'draft').length}
          teamFeedbackLoading={teamRequestsLoading}
          hasDirectReports={hasDirectReports}
        />

        {/* Tab Content */}
        {activeTab === 'my-feedback' && (
          <MyFeedbackView 
            userProfile={userProfile} 
            onRequestCountChange={setMyFeedbackCount}
          />
        )}

        {activeTab === 'team-feedback' && hasDirectReports && (
          <TeamFeedbackView 
            userProfile={userProfile}
            requests={teamRequests}
            requestsLoading={teamRequestsLoading}
            onRefresh={refreshTeamRequests}
          />
        )}
      </main>
    </div>
  );
}